import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type RunProposal = Extract<InFlightAgentActionProposal, { kind: 'custom_agent_run' }>

/**
 * The prompt the query agent handed the specialist. Built-in agents carry the
 * user's wording in the required `question` parameter when no instruction is
 * given; `reason` is always present on chat proposals.
 */
export function customAgentRunPrompt(proposal: RunProposal): string {
  for (const candidate of [proposal.instruction, proposal.parameters?.question, proposal.reason]) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return ''
}

export function CustomAgentRunProposalDetail({ proposal }: { proposal: RunProposal }) {
  const prompt = customAgentRunPrompt(proposal)
  if (!prompt && !proposal.accepted) return null
  return (
    <div className="grid gap-2 rounded-xl border border-line/70 bg-muted/30 p-3 text-11 text-foreground">
      {prompt ? (
        <p className="max-h-64 overflow-auto whitespace-pre-wrap break-words">{prompt}</p>
      ) : null}
      {proposal.accepted ? (
        <p className="text-muted-foreground">
          Accepted. Retry start to recover the saved run if its response was interrupted.
        </p>
      ) : null}
    </div>
  )
}

export const customAgentRunViewCapability: AgentActionViewCapability = {
  kind: 'custom_agent_run',
  applyButtonLabel: (proposal, retry) =>
    retry || (proposal.kind === 'custom_agent_run' && proposal.accepted) ? 'Retry start' : 'Accept',
  rejectLabel: 'Deny',
  badgeLabel: () => 'analysis',
  applyBlockedReason: (proposal) =>
    proposal.kind === 'custom_agent_run' && !proposal.writeId
      ? 'Preparing the saved request.'
      : null,
  rejectBlockedReason: (proposal) =>
    proposal.kind === 'custom_agent_run'
      ? !proposal.writeId
        ? 'Preparing the saved request.'
        : proposal.accepted
          ? 'This run was already accepted.'
          : null
      : null,
  renderDetail: (proposal) =>
    proposal.kind === 'custom_agent_run' ? (
      <CustomAgentRunProposalDetail proposal={proposal} />
    ) : null,
  renderSettledSummary: (proposal) => (
    <p className="px-1 text-10 text-muted-foreground">
      {proposal.terminalState?.kind === 'applied'
        ? 'Accepted — background analysis started.'
        : proposal.terminalState?.kind === 'rejected'
          ? 'Denied — no run started.'
          : 'The run could not be started.'}
    </p>
  )
}
registerAgentActionView(customAgentRunViewCapability)
